import { ActionState, ActionType, Audience, ErrorType } from "@/types";

type ErrorValue = boolean | string | null | undefined;

/** Rules that differ between the audience types' detail pages */
export interface AudiencePageRules {
  readyToBuild: boolean;            // Type specific build requirements, e.g. valid variables or file
  lockWhileBusy: boolean;           // Lock editing while loading, processing or running an action
  buildActions: ActionType[];       // Actions that (re)build the audience
  actionErrors: Partial<Record<ErrorType, ActionType[]>>; // Error banner for each failed action
  extraErrors?: Partial<Record<ErrorType, ErrorValue>>;   // Page specific errors, e.g. other data
}

export interface AudiencePageInput {
  audience?: Audience | null;
  isNew: boolean;
  loading: boolean;                 // Any page data is loading
  loadError: boolean;               // Any page data failed to load
  audienceError?: string | null;    // The audience failed to load
  definition: string;               // The audience's current definition
  currentDefinition: string | null; // The definition it was last loaded, built or saved with
  action: ActionState;
  rules: AudiencePageRules;
}

/**
 * Derives everything an audience detail page needs to know about its state:
 * status flags, which actions are allowed, which is primary, and the error
 * banners to show.
 */
export const deriveAudiencePageState = ({
  audience,
  isNew,
  loading,
  loadError,
  audienceError,
  definition,
  currentDefinition,
  action,
  rules,
}: AudiencePageInput) => {

  // Audience status
  const isShared = !!audience?.shared;
  const isProcessing = audience?.status === 'processing';
  const isComplete = audience?.status === 'complete';
  const isFailed = audience?.status === 'error';
  const isSaved = !!audience && !isNew;

  // Build status
  const isBuilt = !!audience?.audience_size?.length;
  const hasChanged = definition !== currentDefinition;
  const justBuilt = action.status === 'success' && rules.buildActions.includes(action.type);
  const insightIsStale = hasChanged || justBuilt;

  // Activity
  const isActioning = action.status === 'loading';
  const isBusy = loading || loadError || isProcessing || isActioning;
  const isLocked = rules.lockWhileBusy
    ? isBusy || isShared
    : loading || loadError || isShared;

  // The saved, built audience is up to date and nothing is in progress
  const isCurrent = isBuilt && !hasChanged;
  const canShare = isCurrent && isSaved && !isBusy;

  const can = {
    build: hasChanged && rules.readyToBuild && !isLocked && !isActioning,
    insight: isCurrent && !isBusy,
    export: isCurrent && !isBusy && isComplete,
    delete: isCurrent && isSaved && !isLocked && !isActioning,
    share: canShare,
    unshare: canShare && isShared,
    clone: isShared,
  };

  const primary = {
    build: (can.build || (isActioning && action.type === 'build')) && (!isBuilt || hasChanged),
    insight: isCurrent,
    export: isCurrent && isComplete,
  };

  // An action's error is shown until the audience is changed
  const failedWhileCurrent = (types: ActionType[]) =>
    action.status === 'error' && types.includes(action.type) && !hasChanged && action.error;

  const actionErrors = Object.fromEntries(
    Object.entries(rules.actionErrors).map(([key, types]) => [key, failedWhileCurrent(types ?? [])])
  ) as Partial<Record<ErrorType, ErrorValue>>;

  // Key order is the order the banners are shown in
  const errors: Partial<Record<ErrorType, ErrorValue>> = {
    AudienceNotFound: audienceError,
    AudienceError: isFailed,
    ...rules.extraErrors,
    ...actionErrors,
    AudienceProcessing: isProcessing && !isActioning,
    AudienceShared: isShared,
  };

  return {
    isLoading: loading,
    loadError,
    isNew,
    isShared,
    isProcessing,
    isComplete,
    isBuilt,
    hasChanged,
    insightIsStale,
    isBusy,
    isLocked,
    can,
    primary,
    errors,
  };
}

export type AudiencePageState = ReturnType<typeof deriveAudiencePageState>;
